import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import ExcelExportModal from '../components/ExcelExportModal';
import { useAlert } from '../context/AlertContext';

const HazardsList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [hazards, setHazards] = useState([]);
  const [filteredHazards, setFilteredHazards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'All',
    severity: 'All',
    assignee: 'All',
    lti: 'All'
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    fetchHazards();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [hazards, filters]);

  const fetchHazards = async () => {
    try {
      console.log('Starting hazard fetch...');
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      
      const response = await axios.get(`${API}/hazards`);
      console.log('Response received:', response);
      console.log('Hazards count:', response.data?.length);
      console.log('First hazard:', response.data?.[0]);
      
      if (Array.isArray(response.data)) {
        setHazards(response.data);
        console.log('Hazards set successfully:', response.data.length);
      } else {
        console.error('Response data is not an array:', response.data);
        setHazards([]);
      }
    } catch (error) {
      console.error('=== HAZARD FETCH ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error details:', error.response?.data);

      showError(`Failed to fetch hazards: ${error.response?.data?.detail || error.message}. Check browser console for details.`);
      setHazards([]);
    } finally {
      setLoading(false);
      console.log('Fetch complete, loading set to false');
    }
  };

  const applyFilters = () => {
    let filtered = [...hazards];

    if (filters.status !== 'All') {
      filtered = filtered.filter(h => h.status === filters.status);
    }
    if (filters.severity !== 'All') {
      filtered = filtered.filter(h => h.severity === filters.severity);
    }
    if (filters.assignee !== 'All') {
      filtered = filtered.filter(h => h.assignee === filters.assignee);
    }
    if (filters.lti !== 'All') {
      if (filters.lti === 'LTI Only') {
        filtered = filtered.filter(h => h.is_lti === true);
      } else if (filters.lti === 'Non-LTI') {
        filtered = filtered.filter(h => !h.is_lti);
      }
    }

    setFilteredHazards(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleExportExcel = async () => {
    setShowExportModal(true);
    setExportLoading(true);
    
    try {
      // Wait for modal animation to show
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const response = await axios.get(`${API}/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incidents_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait a bit more to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowExportModal(false);

      // Show success message
      setTimeout(() => {
        showSuccess('Excel file downloaded successfully!');
      }, 500);
    } catch (error) {
      console.error('Failed to export:', error);
      setShowExportModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to export data. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const getUniqueValues = (key) => {
    if (!hazards || hazards.length === 0) return ['All'];
    try {
      const values = hazards.map(h => h?.[key]).filter(Boolean);
      return ['All', ...new Set(values)];
    } catch (error) {
      console.error('Error getting unique values:', error);
      return ['All'];
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'In-Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="hazards-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">INCIDENTS LIST</h1>
            <p className="text-gray-600 mt-1">
              {filteredHazards.length} incident(s) found (Total: {hazards.length})
              {' • '}
              <span className="font-semibold text-red-600">
                {hazards.filter(h => h.is_lti).length} LTI(s)
              </span>
            </p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={() => navigate('/report')}
              className="bg-[#1B5C94] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#3AC0DB] transition-colors"
              data-testid="report-incident-button"
            >
              + Report Incident
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportLoading}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400"
              data-testid="export-excel-button"
            >
              {exportLoading ? 'Exporting...' : '📊 Export to Excel'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="status-filter"
              >
                {getUniqueValues('status').map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Severity</label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="severity-filter"
              >
                {getUniqueValues('severity').map(severity => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Assignee</label>
              <select
                value={filters.assignee}
                onChange={(e) => handleFilterChange('assignee', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="assignee-filter"
              >
                {getUniqueValues('assignee').map(assignee => (
                  <option key={assignee} value={assignee}>{assignee}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Lost Time Injury</label>
              <select
                value={filters.lti}
                onChange={(e) => handleFilterChange('lti', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="lti-filter"
              >
                <option value="All">All Incidents</option>
                <option value="LTI Only">LTI Only</option>
                <option value="Non-LTI">Non-LTI</option>
              </select>
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold">#</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Severity</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Assignee</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Due Date</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredHazards.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No incidents found
                    </td>
                  </tr>
                ) : (
                  filteredHazards.map((hazard, index) => (
                    <tr key={hazard?.id || index} className="hover:bg-gray-50 transition-colors" data-testid={`hazard-row-${hazard?.id}`}>
                      <td className="px-6 py-4 text-sm text-gray-700">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-semibold text-gray-800">{hazard?.title || 'Untitled'}</div>
                            <div className="text-xs text-gray-500 mt-1">{hazard?.hazard_type || 'N/A'}</div>
                          </div>
                          {hazard?.is_lti && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">
                              🚨 LTI
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(hazard?.severity)}`}>
                          {hazard?.severity || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(hazard?.status)}`}>
                          {hazard?.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{hazard?.assignee || hazard?.reported_by_name || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{hazard?.due_date || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/hazards/${hazard?.id}`)}
                          className="bg-[#1B5C94] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                          data-testid={`view-hazard-${hazard?.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-4">
            {filteredHazards.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p className="text-lg font-semibold">No incidents found</p>
                <p className="text-sm mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              filteredHazards.map((hazard, index) => (
                <div key={hazard?.id || index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-gray-500">#{index + 1}</div>
                      <div className="text-lg font-semibold text-gray-800 mt-1">{hazard?.title || 'Untitled'}</div>
                      <div className="text-sm text-gray-600 mt-1">{hazard?.hazard_type || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(hazard?.severity)}`}>
                      {hazard?.severity || 'N/A'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(hazard?.status)}`}>
                      {hazard?.status || 'N/A'}
                    </span>
                    {hazard?.is_lti && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">
                        🚨 LTI
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700">
                    <div><span className="font-semibold">Assignee:</span> {hazard?.assignee || hazard?.reported_by_name || 'N/A'}</div>
                    <div><span className="font-semibold">Due:</span> {hazard?.due_date || 'N/A'}</div>
                  </div>
                  <button
                    onClick={() => navigate(`/hazards/${hazard?.id}`)}
                    className="w-full bg-[#1B5C94] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                  >
                    View Details
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Excel Export Progress Modal */}
      <ExcelExportModal 
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
      </div>
  );
};

export default HazardsList;